import round from 'lodash/round';

import {
  Commitment,
  CommitmentAssignment,
  CommitmentClusterAggregatedUsage,
  CommitmentInstanceTypesBasedUsage,
  CommitmentStatus,
  AwsOdcrCommitmentContext,
  AwsCapacityBlockCommitmentContext,
} from '@cast/types';
import { convertUnit, toNumOrUndefined, toNumOrZero, toBig } from '@cast/utils';

import { CLOUD_SERVICE_TO_PROVIDER } from '../../../_constants';
import {
  CommitmentCloudService,
  NormalizedAzureRiCommitment,
  NormalizedAzureSpCommitment,
  NormalizedAwsSpCommitment,
  NormalizedCommitmentBase,
  NormalizedCommitmentUsage,
  NormalizedAwsRiCommitment,
  NormalizedGcpResourceCudCommitment,
  NormalizedResourceCommitmentUsage,
  NormalizedSpendCommitmentUsage,
  NormalizedGcpFlexCudCommitment,
  NormalizedAwsOdcrCommitment,
  NormalizedAwsCapacityBlockCommitment,
  NormalizedGcpReservationCommitment,
} from '../../../types';

/** Rounding precision for multiplication of spend, CPUs and MiB/Bytes */
const PRECISION = 8;

const getAwsOdcrContext = (commitment: Commitment) => {
  return commitment.awsOdcrContext;
};

const getAwsOdcrReservedCpu = (context?: AwsOdcrCommitmentContext) => {
  return toBig(context?.instanceTypeCpu || 0).mul(
    toBig(context?.totalInstanceCount || 0)
  );
};

const getAwsOdcrReservedMemMib = (context?: AwsOdcrCommitmentContext) =>
  toBig(context?.instanceTypeMemoryMib || 0)
    .mul(toBig(context?.totalInstanceCount || 0))
    .toNumber();

const getAwsCapacityBlockContext = (commitment: Commitment) => {
  return commitment.awsCapacityBlockContext;
};

const getAwsCapacityBlockReservedCpu = (
  context?: AwsCapacityBlockCommitmentContext
) => {
  return toBig(context?.instanceTypeCpu || 0).mul(
    toBig(context?.totalInstanceCount || 0)
  );
};

const getAwsCapacityBlockReservedMemMib = (
  context?: AwsCapacityBlockCommitmentContext
) =>
  toBig(context?.instanceTypeMemoryMib || 0)
    .mul(toBig(context?.totalInstanceCount || 0))
    .toNumber();

export const getClusterUtilization = (
  clusters: CommitmentClusterAggregatedUsage[]
) => {
  return clusters.reduce((acc, curr) => {
    acc[curr.clusterId!] ??= curr.usage ?? 0;
    return acc;
  }, {} as Record<string, number>);
};

const normalizeBase = <CS extends CommitmentCloudService>(
  commitment: Commitment,
  assignments: CommitmentAssignment[],
  cloudService: CS
): NormalizedCommitmentBase<CS> => {
  return {
    id: commitment.id!,
    name: commitment.name ?? '-',
    expirationDate: commitment.endDate ?? undefined,
    startDate: commitment.startDate,
    updatedAt: commitment.updatedAt,
    status: commitment.status ?? CommitmentStatus.Inactive,
    state: commitment.state,
    prioritization: commitment.prioritization ?? false,
    autoAssignment: commitment.autoAssignment ?? false,
    allowedUsage: commitment.allowedUsage ?? 1,
    region: commitment.region ?? '-',
    assignments: assignments.filter((a) => a.commitmentId === commitment.id),
    cloudService,
    provider: CLOUD_SERVICE_TO_PROVIDER[cloudService],
  };
};

export const normalizeUsage = <WithInstanceTypes extends boolean>(
  instanceTypesUsage: CommitmentInstanceTypesBasedUsage | undefined
): NormalizedCommitmentUsage<WithInstanceTypes> => {
  if (!instanceTypesUsage) {
    return {
      instanceTypesUsage,
      utilization: 0,
      utilizationByCluster: {},
    };
  }

  return {
    instanceTypesUsage,
    utilization: toNumOrZero(instanceTypesUsage?.usage),
    utilizationByCluster: getClusterUtilization(
      instanceTypesUsage?.clusters || []
    ),
  };
};

export const normalizeResourceUsage = ({
  reservedCpu: _reservedCpu,
  reservedMemMib,
  effectiveCpu,
  effectiveMemoryMib,
  instanceTypesUsage,
}: {
  reservedCpu?: number | string;
  reservedMemMib?: number | string;
  effectiveCpu?: number | string;
  effectiveMemoryMib?: number | string;
  instanceTypesUsage: CommitmentInstanceTypesBasedUsage | undefined;
}): NormalizedResourceCommitmentUsage => {
  const reservedCpu = toNumOrZero(_reservedCpu);
  const reservedMem = convertUnit(toNumOrZero(reservedMemMib), 'MiB', 'Bytes');

  if (!instanceTypesUsage) {
    return {
      reservedCpu,
      reservedMem,
      provisionedCpu: 0,
      provisionedMem: 0,
    };
  }

  const usage: NormalizedResourceCommitmentUsage = {
    reservedCpu,
    reservedMem,
    provisionedCpu: round(
      reservedCpu * toNumOrZero(instanceTypesUsage?.usageDistribution?.cpu),
      PRECISION
    ),
    provisionedMem: round(
      reservedMem * toNumOrZero(instanceTypesUsage?.usageDistribution?.memory),
      PRECISION
    ),
  };
  if (effectiveCpu !== undefined) {
    usage.effectiveReservedCpu = toNumOrZero(effectiveCpu);
  }
  if (effectiveMemoryMib !== undefined) {
    usage.effectiveReservedMem = convertUnit(
      toNumOrZero(effectiveMemoryMib),
      'MiB',
      'Bytes'
    );
  }
  return usage;
};

export const normalizeSpendUsage = ({
  instanceTypesUsage,
  commitmentAmount,
  effectiveCommitmentAmount,
}: {
  instanceTypesUsage: CommitmentInstanceTypesBasedUsage | undefined;
  commitmentAmount?: number;
  effectiveCommitmentAmount?: number;
}): NormalizedSpendCommitmentUsage => {
  const reservedPrice = toNumOrZero(commitmentAmount);
  // fall back to total commitment amount when the effective commitment amount (commitments spent outside of k8s) is not available
  const effectiveCommitmentAmountWithFallback =
    effectiveCommitmentAmount ?? reservedPrice;
  const provisionedPrice = round(
    toNumOrZero(effectiveCommitmentAmountWithFallback) *
      toNumOrZero(instanceTypesUsage?.usage),
    PRECISION
  );

  const provisionedPriceByOthers = round(
    reservedPrice - toNumOrZero(effectiveCommitmentAmountWithFallback),
    PRECISION
  );

  const usage: NormalizedSpendCommitmentUsage = {
    reservedPrice,
    provisionedPrice,
    provisionedPriceByOthers,
  };
  if (effectiveCommitmentAmount !== undefined) {
    usage.effectiveReservedPrice = toNumOrZero(effectiveCommitmentAmount);
  }
  return usage;
};

export const normalizeAzureRiCommitment = <WithInstanceTypes extends boolean>(
  commitment: Commitment,
  assignments: CommitmentAssignment[]
): NormalizedAzureRiCommitment<WithInstanceTypes> => {
  const context = commitment.azureReservationContext;
  const reservedCpu = round(
    toNumOrZero(context?.instanceTypeCpu) * toNumOrZero(context?.count),
    PRECISION
  );
  const reservedMemMib = round(
    toNumOrZero(context?.instanceTypeMemoryMib) * toNumOrZero(context?.count),
    PRECISION
  );

  return {
    ...normalizeBase(commitment, assignments, CommitmentCloudService.AZURE_RI),
    ...normalizeUsage(context?.instanceTypesUsage),
    ...normalizeResourceUsage({
      instanceTypesUsage: context?.instanceTypesUsage,
      reservedCpu,
      reservedMemMib,
    }),
    product: context?.instanceType ?? '-',
    reservedCount: toNumOrZero(context?.count),
    plan: context?.plan,
  };
};

export const normalizeAzureRiCommitments = <WithInstanceTypes extends boolean>(
  commitments: Commitment[],
  assignments: CommitmentAssignment[]
) => {
  return commitments.map((item) =>
    normalizeAzureRiCommitment<WithInstanceTypes>(item, assignments)
  );
};

export const normalizeAzureSpCommitment = <WithInstanceTypes extends boolean>(
  commitment: Commitment,
  assignments: CommitmentAssignment[]
): NormalizedAzureSpCommitment<WithInstanceTypes> => {
  const context = commitment.azureSavingsPlanContext;

  return {
    ...normalizeBase(commitment, assignments, CommitmentCloudService.AZURE_SP),
    ...normalizeUsage(context?.instanceTypesUsage),
    ...normalizeSpendUsage({
      instanceTypesUsage: context?.instanceTypesUsage,
      commitmentAmount: context?.commitmentAmount,
      effectiveCommitmentAmount: context?.effectiveCommitmentAmount,
    }),
    product: context?.id ?? '-',
    planTerm: context?.term,
  };
};

export const normalizeAzureSpCommitments = <WithInstanceTypes extends boolean>(
  commitments: Commitment[],
  assignments: CommitmentAssignment[]
) => {
  return commitments.map((item) =>
    normalizeAzureSpCommitment<WithInstanceTypes>(item, assignments)
  );
};

export const normalizeGcpResourceCudCommitment = <
  WithInstanceTypes extends boolean
>(
  commitment: Commitment,
  assignments: CommitmentAssignment[]
): NormalizedGcpResourceCudCommitment<WithInstanceTypes> => {
  const context = commitment.gcpResourceCudContext;

  return {
    ...normalizeBase(
      commitment,
      assignments,
      CommitmentCloudService.GCP_RESOURCE_CUD
    ),
    ...normalizeUsage(context?.instanceTypesUsage),
    ...normalizeResourceUsage({
      instanceTypesUsage: context?.instanceTypesUsage,
      reservedCpu: context?.cpu,
      reservedMemMib: context?.memoryMb,
    }),
    product: context?.type ?? '-',
    plan: context?.plan,
  };
};

export const normalizeGcpResourceCudCommitments = <
  WithInstanceTypes extends boolean
>(
  commitments: Commitment[],
  assignments: CommitmentAssignment[]
) => {
  return commitments.map((item) =>
    normalizeGcpResourceCudCommitment<WithInstanceTypes>(item, assignments)
  );
};

export const normalizeAwsRiCommitment = <WithInstanceTypes extends boolean>(
  commitment: Commitment,
  assignments: CommitmentAssignment[]
): NormalizedAwsRiCommitment<WithInstanceTypes> => {
  const context = commitment.awsReservedInstancesContext;
  const reservedCpu = round(
    toNumOrZero(context?.instanceTypeCpu) * toNumOrZero(context?.instanceCount),
    PRECISION
  );
  const reservedMemMib = round(
    toNumOrZero(context?.instanceTypeMemoryMib) *
      toNumOrZero(context?.instanceCount),
    PRECISION
  );

  return {
    ...normalizeBase(commitment, assignments, CommitmentCloudService.AWS_RI),
    ...normalizeUsage(context?.instanceTypesUsage),
    ...normalizeResourceUsage({
      instanceTypesUsage: context?.instanceTypesUsage,
      reservedCpu,
      reservedMemMib,
      effectiveCpu: context?.effectiveCpu,
      effectiveMemoryMib: context?.effectiveMemoryMib,
    }),
    product: context?.instanceType ?? '-',
    reservedCount: toNumOrZero(context?.instanceCount),
    availabilityZone: context?.availabilityZoneName ?? '-',
  };
};

export const normalizeAwsRiCommitments = <WithInstanceTypes extends boolean>(
  commitments: Commitment[],
  assignments: CommitmentAssignment[]
) => {
  return commitments.map((item) =>
    normalizeAwsRiCommitment<WithInstanceTypes>(item, assignments)
  );
};

export const normalizeAwsSpCommitment = <WithInstanceTypes extends boolean>(
  commitment: Commitment,
  assignments: CommitmentAssignment[]
): NormalizedAwsSpCommitment<WithInstanceTypes> => {
  const context = commitment.awsSavingsPlanContext;

  return {
    ...normalizeBase(commitment, assignments, CommitmentCloudService.AWS_SP),
    ...normalizeUsage(context?.instanceTypesUsage),
    ...normalizeSpendUsage({
      instanceTypesUsage: context?.instanceTypesUsage,
      commitmentAmount: context?.commitmentAmount,
      effectiveCommitmentAmount: context?.effectiveCommitmentAmount,
    }),
    product: context?.type ?? '-',
    planTerm: context?.commitmentTerm,
    instanceFamily: context?.instanceTypeFamily,
  };
};

export const normalizeAwsSpCommitments = <WithInstanceTypes extends boolean>(
  commitments: Commitment[],
  assignments: CommitmentAssignment[]
) => {
  return commitments.map((item) =>
    normalizeAwsSpCommitment<WithInstanceTypes>(item, assignments)
  );
};

export const normalizeGcpFlexCudCommitment = <
  WithInstanceTypes extends boolean
>(
  commitment: Commitment,
  assignments: CommitmentAssignment[]
): NormalizedGcpFlexCudCommitment<WithInstanceTypes> => {
  const context = commitment.gcpFlexCudContext;
  return {
    ...normalizeBase(
      commitment,
      assignments,
      CommitmentCloudService.GCP_FLEX_CUD
    ),
    ...normalizeUsage<WithInstanceTypes>(context?.instanceTypesUsage),
    ...normalizeSpendUsage({
      instanceTypesUsage: context?.instanceTypesUsage,
      commitmentAmount: context?.commitmentAmount,
      effectiveCommitmentAmount: context?.effectiveCommitmentAmount,
    }),
    product: context?.displayName || '-',
  };
};

export const normalizeGcpFlexCudCommitments = <
  WithInstanceTypes extends boolean
>(
  commitments: Commitment[],
  assignments: CommitmentAssignment[]
): Array<NormalizedGcpFlexCudCommitment<WithInstanceTypes>> => {
  return commitments.map((item) =>
    normalizeGcpFlexCudCommitment<WithInstanceTypes>(item, assignments)
  );
};

export const normalizeAwsOdcrCommitment = <WithInstanceTypes extends boolean>(
  commitment: Commitment,
  assignments: CommitmentAssignment[]
): NormalizedAwsOdcrCommitment<WithInstanceTypes> => {
  const context = getAwsOdcrContext(commitment);

  const reservedCpu = round(
    getAwsOdcrReservedCpu(context).toNumber(),
    PRECISION
  );
  const reservedMemMib = round(getAwsOdcrReservedMemMib(context), PRECISION);

  return {
    ...normalizeBase(commitment, assignments, CommitmentCloudService.AWS_ODCR),
    ...normalizeUsage(context?.instanceTypesUsage),
    ...normalizeResourceUsage({
      instanceTypesUsage: context?.instanceTypesUsage,
      reservedCpu,
      reservedMemMib,
    }),
    product: context?.instanceType || '-',
    quantity: toNumOrUndefined(context?.totalInstanceCount),
    isTargeted: context?.instanceMatchCriteria === 'targeted',
    awsCommitmentId: context?.id || '',
  };
};

export const normalizeAwsOdcrCommitments = <WithInstanceTypes extends boolean>(
  commitments: Commitment[],
  assignments: CommitmentAssignment[]
) => {
  return commitments.map((item) =>
    normalizeAwsOdcrCommitment<WithInstanceTypes>(item, assignments)
  );
};

export const normalizeAwsCapacityBlockCommitment = <
  WithInstanceTypes extends boolean
>(
  commitment: Commitment,
  assignments: CommitmentAssignment[]
): NormalizedAwsCapacityBlockCommitment<WithInstanceTypes> => {
  const context = getAwsCapacityBlockContext(commitment);

  const reservedCpu = round(
    getAwsCapacityBlockReservedCpu(context).toNumber(),
    PRECISION
  );
  const reservedMemMib = round(
    getAwsCapacityBlockReservedMemMib(context),
    PRECISION
  );

  return {
    ...normalizeBase(
      commitment,
      assignments,
      CommitmentCloudService.AWS_CAPACITY_BLOCK
    ),
    ...normalizeUsage(context?.instanceTypesUsage),
    ...normalizeResourceUsage({
      instanceTypesUsage: context?.instanceTypesUsage,
      reservedCpu,
      reservedMemMib,
    }),
    product: context?.instanceType || '-',
    quantity: toNumOrUndefined(context?.totalInstanceCount),
    awsCommitmentId: context?.id || '',
  };
};

export const normalizeAwsCapacityBlockCommitments = <
  WithInstanceTypes extends boolean
>(
  commitments: Commitment[],
  assignments: CommitmentAssignment[]
) => {
  return commitments.map((item) =>
    normalizeAwsCapacityBlockCommitment<WithInstanceTypes>(item, assignments)
  );
};

const getGcpReservationContext = (commitment: Commitment) => {
  return commitment.gcpCapacityReservationContext;
};

export const normalizeGcpReservationCommitment = <
  WithInstanceTypes extends boolean
>(
  commitment: Commitment,
  assignments: CommitmentAssignment[]
): NormalizedGcpReservationCommitment<WithInstanceTypes> => {
  const context = getGcpReservationContext(commitment);

  const reservedCpu = round(
    toBig(context?.instanceTypeCpu || 0)
      .mul(toBig(context?.totalInstanceCount || 0))
      .toNumber(),
    PRECISION
  );
  const reservedMemMib = round(
    toBig(context?.instanceTypeMemoryMib || 0)
      .mul(toBig(context?.totalInstanceCount || 0))
      .toNumber(),
    PRECISION
  );

  return {
    ...normalizeBase(
      commitment,
      assignments,
      CommitmentCloudService.GCP_RESERVATION
    ),
    ...normalizeUsage(context?.instanceTypesUsage),
    ...normalizeResourceUsage({
      instanceTypesUsage: context?.instanceTypesUsage,
      reservedCpu,
      reservedMemMib,
      effectiveCpu: context?.effectiveCpu,
      effectiveMemoryMib: context?.effectiveMemoryMib,
    }),
    product: context?.instanceType || '-',
    quantity: toNumOrUndefined(context?.totalInstanceCount),
    isSpecific: context?.specificReservationRequired ?? false,
    gcpCommitmentId: context?.id || '',
  };
};

export const normalizeGcpReservationCommitments = <
  WithInstanceTypes extends boolean
>(
  commitments: Commitment[],
  assignments: CommitmentAssignment[]
) => {
  return commitments.map((item) =>
    normalizeGcpReservationCommitment<WithInstanceTypes>(item, assignments)
  );
};
